import { apiCall } from ".";
import { getSession } from "../handlers/getSession";
import { getTenant } from "../handlers/getTenant";

/**
 * Custom Dashboards — per-user BI dashboards (casemanager.CustomDashboard).
 *
 * These dashboards store the MATERIALIZED RESULT of each piece (a frozen
 * snapshot in `content`), so the viewer renders without ever re-querying EQL.
 * A "Refresh" action re-materializes from each piece's stored `sourceRef`.
 *
 * Base: /{tenant}/api/custom_dashboard/{list,detail,create,update,delete}/
 * Every request is scoped to the authenticated user server-side; a user only
 * ever sees or edits their own dashboards. Requires both auth headers.
 */

const path = (tenant, suffix) => `${tenant}/api/custom_dashboard/${suffix}`;

const authHeaders = (session) => ({
  IdToken: session.IdToken,
  Authorization: `Bearer ${session.AccessToken}`,
});

/** @param {"one_page"|"slides"} [type] optional filter */
export const listCustomDashboards = async (type) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.get(path(tenant, "list/"), {
    params: type ? { type } : undefined,
    headers: authHeaders(session),
  });
  return resp.data;
};

export const getCustomDashboard = async (id) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.get(path(tenant, "detail/"), {
    params: { id },
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * @param {Object} payload
 * @param {string} payload.name
 * @param {Object} [payload.content]  free-form JSON (default {})
 * @param {"one_page"|"slides"} [payload.type]  default "one_page"
 */
export const createCustomDashboard = async ({ name, content, type }) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const body = { name };
  if (content !== undefined) body.content = content;
  if (type !== undefined) body.type = type;

  const resp = await apiCall.post(path(tenant, "create/"), body, {
    headers: authHeaders(session),
  });
  return resp.data;
};

/**
 * Partial update — only the fields sent are changed. `content` replaces the
 * whole value.
 * @param {Object} payload  { id, name?, content?, type? }
 */
export const updateCustomDashboard = async ({ id, name, content, type }) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const body = { id };
  if (name !== undefined) body.name = name;
  if (content !== undefined) body.content = content;
  if (type !== undefined) body.type = type;

  const resp = await apiCall.patch(path(tenant, "update/"), body, {
    headers: authHeaders(session),
  });
  return resp.data;
};

/** Soft delete (marks deleted_at). */
export const deleteCustomDashboard = async (id) => {
  const session = getSession();
  const tenant = getTenant();
  if (!session) return null;

  const resp = await apiCall.delete(path(tenant, "delete/"), {
    data: { id },
    headers: authHeaders(session),
  });
  return resp.data;
};
